import { FC, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { CallIntelConstant, CallIntelligenceV2, CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { Trans, useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { useAppFlagStore } from '@frontend/shared';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Heading, Text } from '@frontend/design-system';
import { featureFlags } from '../../../../feature-flags';
import { SentimentsChart } from '../../charts/sentiments-chart';
import { ServiceIssueChart } from '../../charts/service-issue.chart';
import { useActiveFilters } from '../../hooks/use-active-filters';
import { useCallIntelShallowStore } from '../../hooks/use-call-intel-store';
import { useTotalCallsAnalyzedCount } from '../../hooks/use-total-calls-analyzed-count';

export const PatientOverview: FC = () => {
  const { t } = useTranslation('call-intelligence');
  const { patientCapitalized: patientCapitalizedToken } = useTranslationTokens();

  const { setIsLoadingAggregatedData } = useCallIntelShallowStore('setIsLoadingAggregatedData');
  const { filters, filterHintText } = useActiveFilters();
  const { getFeatureFlagValue } = useAppFlagStore();
  const isServiceQualityEnabled = getFeatureFlagValue({
    flagName: featureFlags.enableServiceQuality,
    locationIds: filters.locations || [],
    strategy: 'ANY',
  });

  const { totalCallsAnalyzed } = useTotalCallsAnalyzedCount();

  const { inView: isVisible, ref } = useInView({ triggerOnce: true });

  const { overviewRequestPayload, serviceQualityStatsRequestPayload } = useMemo(() => {
    const overviewRequestPayload = {
      ...CallIntelUtils.generateChartsPayload({ filters }),
      includesAnalysis: [CallIntelTypes.IncludesAnalysis.CALL_SENTIMENT],
      includesHydration: [],
    };

    const serviceQualityStatsRequestPayload = {
      ...CallIntelUtils.getServiceQualityStatsPayload({
        filters: {
          ...filters,
          serviceQualityFlags: [
            CallIntelTypes.ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT,
            CallIntelTypes.ServiceQualityFlagEnum.FLAG_REPEAT_CALLER,
            CallIntelTypes.ServiceQualityFlagEnum.FLAG_CALL_ESCALATION,
          ],
        },
        dateRangeParams: { timePeriod: CallIntelUtils.convertTimePeriodLabelToEnum(filterHintText) },
      }),
    };
    return { overviewRequestPayload, serviceQualityStatsRequestPayload };
  }, [filters, filterHintText]);

  const { data: overviewData, isLoading: isOverviewLoading } = CallIntelligenceV2.Queries.useGetCallsOverviewQuery({
    request: overviewRequestPayload,
    options: {
      enabled: !!filters.locations?.length && isVisible,
      retry: false,
      cacheTime: CallIntelConstant.DEFAULT_CACHE_TIME,
      staleTime: CallIntelConstant.DEFAULT_CACHE_TIME,
      onError: (error) => {
        console.error('Error fetching calls overview data for patient experience:', error);
        sentry.warn({
          team: 'analytics',
          topic: 'call-intelligence',
          error: `Error fetching calls overview data for patient experience: ${error}`,
          addContext: {
            name: 'filters',
            context: { payload: overviewRequestPayload },
          },
        });
      },
    },
  });

  const { data: serviceQualityStats, isLoading: isServiceQualityStatsLoading } =
    CallIntelligenceV2.Queries.useGetServiceQualityStatsQuery({
      request: serviceQualityStatsRequestPayload,
      options: {
        enabled:
          !!filters.locations?.length &&
          !!filters.startDate &&
          !!filters.endDate &&
          isServiceQualityEnabled &&
          isVisible,
        retry: false,
        cacheTime: CallIntelConstant.DEFAULT_CACHE_TIME,
        staleTime: CallIntelConstant.DEFAULT_CACHE_TIME,
        onError: (error) => {
          console.error('Error fetching calls service quality stats:', error);
          sentry.warn({
            team: 'analytics',
            topic: 'call-intelligence',
            error: `Error fetching calls service quality stats data for patient experience: ${error}`,
            addContext: {
              name: 'filters',
              context: { payload: serviceQualityStatsRequestPayload },
            },
          });
        },
      },
    });

  useEffect(() => {
    setIsLoadingAggregatedData?.(isOverviewLoading);
  }, [isOverviewLoading, setIsLoadingAggregatedData]);

  return (
    <section css={defaultStyles.container} ref={ref} data-testid='patient-experience-section'>
      <div css={defaultStyles.headerContainer}>
        <Heading level={3}>{t('{{patientCapitalizedToken}} Experience', { patientCapitalizedToken })}</Heading>
        <Text size='medium' color='subdued'>
          <Trans t={t} count={totalCallsAnalyzed} values={{ totalCallsAnalyzed }}>
            <strong>{{ totalCallsAnalyzed }}</strong> calls analyzed
          </Trans>
        </Text>
      </div>
      <div css={defaultStyles.cardContainer}>
        {isServiceQualityEnabled ? (
          <>
            <SentimentsChart
              isLoading={isOverviewLoading}
              sentiments={overviewData?.sentiments}
              stats={serviceQualityStats}
              showChartInsights
            />
            <ServiceIssueChart isLoading={isServiceQualityStatsLoading} serviceQualityStats={serviceQualityStats} />
          </>
        ) : (
          <div
            css={css`
              grid-column: span 2;
            `}
          >
            <SentimentsChart isLoading={isOverviewLoading} sentiments={overviewData?.sentiments} />
          </div>
        )}
      </div>
    </section>
  );
};

const defaultStyles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  headerContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  cardContainer: css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: ${theme.spacing(3)};
  `,
};
